import React, { useEffect, useMemo, useRef } from "react";
import CheckoutPage from "./CheckoutPage";

import { useCheckoutForm, CheckoutFormTypeFields } from "../components/Checkout/hooks/useCheckoutForm";
import { useCheckoutResume } from "@ecommerce/checkout";

import { OrderPersonalInformationFields, OrderDeliveryAddressFields } from "../types/purchaseOrder/Order";

const setValueSilent = (methods: any, path: string, value: any) => {
    methods.setValue(path as any, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
    });
};

const setValueSilentValidate = (methods: any, path: string, value: any) => {
    methods.setValue(path as any, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
    });
};

const anyTruthy = (obj: unknown): boolean => {
    if (!obj || typeof obj !== "object") return false;

    return Object.values(obj as Record<string, unknown>).some((v) => {
        if (v && typeof v === "object") return anyTruthy(v);
        return !!v;
    });
};

const CheckoutResumePage: React.FC = () => {
    const { resume } = useCheckoutResume();
    const {
        methods,
        confirmedZipCode,
        isHomeDelivery,
        deliveryType,
        deliveryOptions,
        couponStatus,
        applyCoupon,
        onSubmitZip,
        setDeliveryType,
    } = useCheckoutForm();

    const hydratedBaseRef = useRef(false);
    const autoZipSubmittedRef = useRef(false);
    const initialShippingAppliedRef = useRef(false);
    const couponAutoAppliedRef = useRef(false);

    const orden = resume.Orden;

    const desiredShippingOptionId = useMemo(() => orden?.idEnvioOpcion || null, [orden]);
    const desiredCouponName = useMemo(() => orden?.nombreCupon || null, [orden]);

    const desiredDeliveryValue = useMemo(() => {
        const opt = orden?.idEnvioOpcion;
        if (!opt) return null;

        const branch = (orden as any)?.idEnvioSucursal ?? null;
        return `${opt}::${branch ?? ""}`;
    }, [orden]);

    useEffect(() => {
        if (hydratedBaseRef.current) return;
        hydratedBaseRef.current = true;

        setValueSilent(methods, `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Email}`, orden.mail || "");
        setValueSilent(methods, `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.FirstName}`, orden.nombre ?? "");
        setValueSilent(methods, `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.LastName}`, orden.apellido ?? "");
        setValueSilent(methods, `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Document}`, orden.documento ?? "");
        setValueSilent(methods, `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.AreaCode}`, orden.codigoArea ?? "");
        setValueSilent(methods, `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.PhoneNumber}`, orden.telefono ?? "");

        setValueSilent(methods, `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}`, orden.CP || "");
    }, [methods, orden]);

    useEffect(() => {
        if (confirmedZipCode) return;
        if (autoZipSubmittedRef.current) return;

        const mail = methods.getValues(
            `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Email}` as any
        );
        const cp = methods.getValues(`${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}` as any);

        if (!mail || !cp) return;

        autoZipSubmittedRef.current = true;
        onSubmitZip();
    }, [confirmedZipCode, methods, onSubmitZip]);

  useEffect(() => {
    if (initialShippingAppliedRef.current) return;
    if (!desiredDeliveryValue) return;
    if (!confirmedZipCode) return;
    if (!deliveryOptions?.length) return;

    const current = methods.getValues(CheckoutFormTypeFields.DeliveryType as any);
    if (current) {
      initialShippingAppliedRef.current = true;
      return;
    }

    const exists = deliveryOptions.some((g: any) =>
      (g.options || []).some((o: any) => `${o.value}` === `${desiredDeliveryValue}`)
    );

    if (!exists) {
      initialShippingAppliedRef.current = true;
      return;
    }

    setDeliveryType(desiredDeliveryValue);

    initialShippingAppliedRef.current = true;
  }, [confirmedZipCode, deliveryOptions, desiredDeliveryValue, methods, setDeliveryType]);

    useEffect(() => {
        if (!confirmedZipCode) return;
        if (!isHomeDelivery) return;

        const dirtyAddress = (methods.formState.dirtyFields as any)?.[CheckoutFormTypeFields.DeliveryAddress];
        if (anyTruthy(dirtyAddress)) return;

        const hasAny =
            !!orden.calle ||
            !!orden.calleNro ||
            !!orden.pisoDpto ||
            (orden.codProvincia !== null && orden.codProvincia !== undefined) ||
            !!orden.descLocalidad;

        if (!hasAny) return;

        const t = window.setTimeout(() => {
            const dirtyAgain = (methods.formState.dirtyFields as any)?.[CheckoutFormTypeFields.DeliveryAddress];
            if (anyTruthy(dirtyAgain)) return;

            setValueSilent(methods, `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.Street}`, orden.calle ?? "");
            setValueSilent(methods, `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.StreetNumber}`, orden.calleNro ?? "");
            setValueSilent(methods, `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.FloorApartment}`, orden.pisoDpto ?? "");
            setValueSilent(methods, `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ProvinceCode}`, orden.codProvincia ?? 0);
            setValueSilent(methods, `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.LocalityDesc}`, orden.descLocalidad ?? "");
        }, 0);

        return () => window.clearTimeout(t);
    }, [confirmedZipCode, isHomeDelivery, deliveryType, methods, orden]);

    useEffect(() => {
        if (!desiredCouponName) return;
        if (!confirmedZipCode) return;
        if (!deliveryType) return;

        if (couponAutoAppliedRef.current) return;
        if (couponStatus?.loading) return;

        if (couponStatus?.appliedDiscount) {
            couponAutoAppliedRef.current = true;
            return;
        }

        couponAutoAppliedRef.current = true;
        applyCoupon(desiredCouponName);
    }, [desiredCouponName, confirmedZipCode, deliveryType, couponStatus, applyCoupon]);

    return <CheckoutPage />;
};

export default CheckoutResumePage;














